import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  isCanceledRequest,
  prospectionErrorText,
} from 'helper/prospectionErrors';
import useText from 'hooks/useText';
import {
  getCallDay,
  getGlobalList,
  openDaySession,
} from 'server/crm/prospectionApi';

/**
 * The data of today's call list. On opening, the day session is opened or
 * resumed (R14) before the list is read (R7): the server fills the day when the
 * session opens. The global list feeds both its panel and the suggestions.
 * `now` is when the day was last read. A failed day gives `error` (translated); a failed global list a toast and [].
 */
const useCallDay = () => {
  const { text } = useText('prospection');
  const [day, setDay] = useState(null);
  // The moment the day was read: "today" for the labels and the suggestions.
  const [now, setNow] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalProspects, setGlobalProspects] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const dayFailed = useCallback(
    (failure) => {
      if (!alive.current || isCanceledRequest(failure)) return;
      setError(prospectionErrorText(text, failure));
    },
    [text],
  );

  const showDay = useCallback((next) => {
    if (!alive.current) return;
    setDay(next);
    setNow(new Date());
    setError(null);
  }, []);

  const reloadDay = useCallback(
    () => getCallDay().then(showDay, dayFailed),
    [showDay, dayFailed],
  );

  const reloadGlobal = useCallback(() => {
    setGlobalLoading(true);
    return getGlobalList()
      .then(
        (result) => {
          if (alive.current) setGlobalProspects(result?.prospects || []);
        },
        (failure) => {
          if (!alive.current || isCanceledRequest(failure)) return;
          toast.error(prospectionErrorText(text, failure));
          setGlobalProspects([]);
        },
      )
      .finally(() => {
        if (alive.current) setGlobalLoading(false);
      });
  }, [text]);

  useEffect(() => {
    setLoading(true);
    openDaySession()
      .then(() => {
        // Read after the session opens: the day it fills leaves the global list.
        reloadGlobal();
        return getCallDay();
      })
      .then(showDay, dayFailed)
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  }, [reloadGlobal, showDay, dayFailed]);

  return {
    day,
    now,
    loading,
    error,
    reloadDay,
    globalProspects,
    globalLoading,
    reloadGlobal,
  };
};

export default useCallDay;
